import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Alert,
  IconButton,
  useTheme,
  alpha,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { Case } from '../../../types';
import { updateCase } from '../../../services/firebase';
import { formatDate } from '../../../utils/formatters';
import { toast } from 'react-toastify';

interface EditBiometricDialogProps {
  open: boolean;
  onClose: () => void;
  caseData: Case;
}

const EditBiometricDialog: React.FC<EditBiometricDialogProps> = ({
  open,
  onClose,
  caseData,
}) => {
  const theme = useTheme();
  const [biometricDate, setBiometricDate] = useState(
    caseData.biometricDate || new Date().toISOString().split('T')[0]
  );
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async () => {
    try {
      setIsSubmitting(true);
      setError(null);

      await updateCase(caseData.id, {
        biometricTaken: true,
        biometricDate: biometricDate,
      });

      toast.success('Biometric date updated successfully');
      onClose();
      window.location.reload(); // Refresh to show updated data
    } catch (error) {
      console.error('Error updating biometric date:', error);
      setError('Failed to update biometric date. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        elevation: 0,
        sx: {
          borderRadius: '16px',
          bgcolor: 'background.paper',
          boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.08)}`,
        },
      }}
    >
      <DialogTitle>
        Edit Biometric Date
        <IconButton
          onClick={onClose}
          aria-label="Close"
          size="small"
          sx={{ position: 'absolute', right: 12, top: 12, color: 'text.secondary' }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Current Biometric Date: {caseData.biometricDate ? formatDate(caseData.biometricDate) : 'Not set'}
          </Typography>
        </Box>

        <TextField
          label="Biometric Date"
          type="date"
          value={biometricDate}
          onChange={(e) => setBiometricDate(e.target.value)}
          fullWidth
          InputLabelProps={{ shrink: true }}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: '12px',
              bgcolor: 'background.paper',
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: alpha(theme.palette.primary.main, 0.2),
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: theme.palette.primary.main,
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: theme.palette.primary.main,
              },
            },
          }}
        />
      </DialogContent>
      <DialogActions sx={{ p: 2.5 }}>
        <Button
          onClick={onClose}
          sx={{
            borderRadius: '12px',
            color: 'text.secondary',
            '&:hover': {
              bgcolor: alpha(theme.palette.text.secondary, 0.08),
            },
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={isSubmitting}
          sx={{
            borderRadius: '12px',
            boxShadow: 'none',
            '&:hover': {
              boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.2)}`,
            },
          }}
        >
          {isSubmitting ? 'Updating...' : 'Update'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditBiometricDialog; 